import React, { useState } from 'react';
import { submitLead, isValidIndianMobile, isValidPincode, isValidEmail } from '../../utils/leads';
import { ArrowRight, Check, Clock, Shield } from '../../components/ui/Icons';

interface FormValues {
  name: string;
  mobile: string;
  pincode: string;
  email: string;
  whatsappUpdates: boolean;
  constructionWork: boolean;
  localPainter: boolean;
}

type TextField = 'name' | 'mobile' | 'pincode' | 'email';
type Errors = Partial<Record<TextField, string>>;
type Status = 'idle' | 'submitting' | 'success' | 'error';

const INITIAL: FormValues = {
  name: '',
  mobile: '',
  pincode: '',
  email: '',
  whatsappUpdates: true,
  constructionWork: false,
  localPainter: false,
};

const validateField = (field: TextField, value: string): string | undefined => {
  const v = value.trim();
  switch (field) {
    case 'name':
      return v.length < 2 ? 'Please enter your name.' : undefined;
    case 'mobile':
      return isValidIndianMobile(v) ? undefined : 'Enter a valid 10-digit Indian mobile number.';
    case 'pincode':
      return isValidPincode(v) ? undefined : 'Enter a valid 6-digit PIN code.';
    case 'email':
      return isValidEmail(v) ? undefined : 'Enter a valid email address.';
    default:
      return undefined;
  }
};

const validateAll = (values: FormValues): Errors => {
  const errors: Errors = {};
  (['name', 'mobile', 'pincode', 'email'] as TextField[]).forEach((f) => {
    const msg = validateField(f, values[f]);
    if (msg) errors[f] = msg;
  });
  return errors;
};

const TRUST = [
  { icon: Shield, label: 'No spam, ever' },
  { icon: Clock, label: 'Response within 24 hours' },
];

/**
 * "Get Expert Advice" lead form. Self-contained navy panel — drop it inside
 * any `container`. Submissions go through `submitLead()` (EmailJS pipeline).
 */
const Form: React.FC = () => {
  const [values, setValues] = useState<FormValues>(INITIAL);
  const [errors, setErrors] = useState<Errors>({});
  const [touched, setTouched] = useState<Partial<Record<TextField, boolean>>>({});
  const [status, setStatus] = useState<Status>('idle');
  const [serverError, setServerError] = useState<string>('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setValues((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (type !== 'checkbox' && touched[name as TextField]) {
      setErrors((prev) => ({ ...prev, [name]: validateField(name as TextField, value) }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const field = e.target.name as TextField;
    setTouched((prev) => ({ ...prev, [field]: true }));
    setErrors((prev) => ({ ...prev, [field]: validateField(field, e.target.value) }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const nextErrors = validateAll(values);
    setTouched({ name: true, mobile: true, pincode: true, email: true });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      const first = Object.keys(nextErrors)[0];
      document.getElementById(`expert-${first}`)?.focus();
      return;
    }

    setStatus('submitting');
    setServerError('');
    const result = await submitLead({
      source: 'home-expert-advice',
      name: values.name.trim(),
      mobile: values.mobile.trim(),
      email: values.email.trim(),
      pincode: values.pincode.trim(),
      'WhatsApp updates': values.whatsappUpdates ? 'Yes' : 'No',
      'Construction work at site': values.constructionWork ? 'Yes' : 'No',
      'Local painter hired': values.localPainter ? 'Yes' : 'No',
    });

    if (result.ok) {
      setStatus('success');
      setValues(INITIAL);
      setTouched({});
      setErrors({});
    } else {
      setStatus('error');
      setServerError(result.error);
    }
  };

  const reset = () => {
    setStatus('idle');
    setServerError('');
  };

  const fieldProps = (field: TextField) => ({
    id: `expert-${field}`,
    name: field,
    value: values[field],
    onChange: handleChange,
    onBlur: handleBlur,
    'aria-invalid': Boolean(errors[field]) || undefined,
    'aria-describedby': errors[field] ? `expert-${field}-error` : undefined,
    className: `vp-input vp-input-dark ${errors[field] ? '!border-brand-red/70' : ''}`,
  });

  const ErrorText = ({ field }: { field: TextField }) =>
    errors[field] ? (
      <p id={`expert-${field}-error`} className="mt-1.5 text-xs text-[#ffb4bc]" role="alert">
        {errors[field]}
      </p>
    ) : null;

  return (
    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-navy via-[#141b4a] to-brand-blue text-white shadow-lift">
      <div className="pointer-events-none absolute -top-24 -right-16 w-80 h-80 rounded-full bg-brand-gold/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-brand-red/20 blur-3xl" />

      <div className="relative grid lg:grid-cols-12 gap-10 lg:gap-14 p-6 sm:p-10 lg:p-14">
        {/* Intro */}
        <div className="lg:col-span-5">
          <span className="vp-eyebrow vp-eyebrow-light">Get expert advice</span>
          <h2 className="vp-heading !text-white mt-4 text-3xl sm:text-4xl">Talk to a Vertex expert before you buy a single tin</h2>
          <p className="mt-4 text-white/70 leading-relaxed">
            Tell us where you are and what you are painting. A Vertex advisor will call you back with product, colour and applicator recommendations for your site.
          </p>

          <ul className="mt-8 space-y-3">
            {TRUST.map((t) => {
              const Icon = t.icon;
              return (
                <li key={t.label} className="flex items-center gap-3 text-sm text-white/85">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-white/10 border border-white/15 text-brand-gold flex items-center justify-center">
                    <Icon size={15} />
                  </span>
                  {t.label}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Form / success */}
        <div className="lg:col-span-7">
          {status === 'success' ? (
            <div className="vp-glass rounded-2xl p-8 sm:p-10 h-full flex flex-col items-start justify-center" role="status" aria-live="polite">
              <span className="w-14 h-14 rounded-full bg-brand-gold text-brand-ink flex items-center justify-center shadow-gold">
                <Check size={26} />
              </span>
              <h3 className="mt-6 text-2xl font-bold text-white">Request received</h3>
              <p className="mt-3 text-white/75 leading-relaxed max-w-md">
                Thank you. A Vertex advisor will get in touch within 24 hours. If it is urgent, email{' '}
                <a href="mailto:support@vertexpaint.com" className="font-semibold text-brand-gold">
                  support@vertexpaint.com
                </a>
                .
              </p>
              <button type="button" onClick={reset} className="vp-btn vp-btn-ghost mt-8">
                Send another request
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="expert-name" className="vp-label !text-white/85">
                    Full name <span className="text-brand-gold">*</span>
                  </label>
                  <input type="text" placeholder="Your name" autoComplete="name" {...fieldProps('name')} />
                  <ErrorText field="name" />
                </div>
                <div>
                  <label htmlFor="expert-mobile" className="vp-label !text-white/85">
                    Mobile number <span className="text-brand-gold">*</span>
                  </label>
                  <input type="tel" inputMode="numeric" placeholder="10-digit mobile number" autoComplete="tel-national" {...fieldProps('mobile')} />
                  <ErrorText field="mobile" />
                </div>
                <div>
                  <label htmlFor="expert-pincode" className="vp-label !text-white/85">
                    PIN code <span className="text-brand-gold">*</span>
                  </label>
                  <input type="text" inputMode="numeric" maxLength={6} placeholder="6-digit PIN code" autoComplete="postal-code" {...fieldProps('pincode')} />
                  <ErrorText field="pincode" />
                </div>
                <div>
                  <label htmlFor="expert-email" className="vp-label !text-white/85">
                    Email <span className="text-brand-gold">*</span>
                  </label>
                  <input type="email" placeholder="you@example.com" autoComplete="email" {...fieldProps('email')} />
                  <ErrorText field="email" />
                </div>
              </div>

              <fieldset className="mt-6">
                <legend className="vp-label !text-white/85">About your project</legend>
                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="flex items-start gap-3 text-sm text-white/80 cursor-pointer">
                    <input type="checkbox" name="constructionWork" checked={values.constructionWork} onChange={handleChange} className="vp-checkbox mt-0.5 !accent-brand-gold" />
                    There is construction work going on at my house
                  </label>
                  <label className="flex items-start gap-3 text-sm text-white/80 cursor-pointer">
                    <input type="checkbox" name="localPainter" checked={values.localPainter} onChange={handleChange} className="vp-checkbox mt-0.5 !accent-brand-gold" />
                    I have already hired a local painter
                  </label>
                  <label className="flex items-start gap-3 text-sm text-white/80 cursor-pointer sm:col-span-2">
                    <input type="checkbox" name="whatsappUpdates" checked={values.whatsappUpdates} onChange={handleChange} className="vp-checkbox mt-0.5 !accent-brand-gold" />
                    Send me updates on WhatsApp
                  </label>
                </div>
              </fieldset>

              {status === 'error' && serverError && (
                <p className="mt-5 rounded-xl border border-brand-red/50 bg-brand-red/15 px-4 py-3 text-sm text-white" role="alert">
                  {serverError}
                </p>
              )}

              <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
                <button type="submit" disabled={status === 'submitting'} className="vp-btn vp-btn-gold text-base w-full sm:w-auto">
                  {status === 'submitting' ? (
                    <>
                      <span className="w-4 h-4 rounded-full border-2 border-brand-ink/30 border-t-brand-ink animate-spin" aria-hidden="true" />
                      Sending…
                    </>
                  ) : (
                    <>
                      Request a call back
                      <ArrowRight />
                    </>
                  )}
                </button>
                <p className="text-xs text-white/55 leading-relaxed sm:max-w-xs">
                  By proceeding you authorise Vertex Paints and its recommended contractors to contact you by call, SMS, WhatsApp or email about your enquiry.
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Form;
